import type { SubitemKind } from '@/types/PolicySublimit';

export interface AIModel {
  arn: string;
  name: string;
  description: string;
  isActive?: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface InvokeModelRequest {
  modelId: string;
  converseInput: ConverseInput;
  s3Key?: string; // For backward compatibility
  documentId?: string; // Platform document ID (UUID from Document.DocumentID)
  expectedOutputSchema?: string;
  promptId?: number; // Optional prompt ID (for async invocations)
  promptVersion?: string; // Optional prompt version (for async invocations)
}

// Unified Bedrock Converse request shape — same for every model family.
// BE translates per-model server-side.
export interface ConverseInput {
  messages: ConverseMessage[];
  system?: ConverseSystemMessage[];
  inferenceConfig?: ConverseInferenceConfig;
}

export interface ConverseMessage {
  role: 'user' | 'assistant';
  content: ConverseContentBlock[];
}

export interface ConverseContentBlock {
  text: string;
}

export interface ConverseSystemMessage {
  text: string;
}

export interface ConverseInferenceConfig {
  maxTokens?: number;
  temperature?: number;
  topP?: number;
  stopSequences?: string[];
}

export function newConverseInput(userPrompt: string, systemPrompt?: string): ConverseInput {
  const input: ConverseInput = {
    messages: [{ role: 'user', content: [{ text: userPrompt }] }],
  };
  if (systemPrompt) {
    input.system = [{ text: systemPrompt }];
  }
  return input;
}

export interface InvokeModelResponse {
  text: string;
  inputTokensUsed: number;
  outputTokensUsed: number;
}

// Internal prompts feed the coverage-line flows (prompt mapping, binder
// processing); external prompts are excluded from them and serve the external
// policy upload path. Chosen at creation, immutable after.
export type PromptDesignation = 'internal' | 'external';

export interface AIPrompt {
  promptVersionId: number;
  promptId: number | string;
  name: string;
  version: string;
  description: string;
  designation: PromptDesignation;
  systemPrompt: string;
  userPrompt: string;
  temperature: string;
  maxTokens: number;
  modelArn?: string;
  isDraft: boolean;
  expectedOutputSchema?: string; // JSON string for expected output schema
  requiresCarrierContext?: boolean;
  requiresCoverageTypeContext?: boolean;
  requiresWholesalerContext?: boolean;
  publishedBy?: number;
  publishedByName?: string;
  createdBy?: number;
  createdByName?: string;
  createdAt: string; // ISO 8601 date string
  updatedAt: string; // ISO 8601 date string
  deletedAt: string; // ISO 8601 date string
}

export interface PromptVariant {
  modelId?: string;
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  stopSequences?: string[];
}

export interface CreatePromptRequest {
  name: string; // The name of the prompt
  description: string; // A description of what the prompt does
  designation?: PromptDesignation; // Defaults to 'internal' server-side
  userPrompt: string; // The actual prompt text content
  systemPrompt?: string; // Optional system prompt
  modelArn?: string; // Optional model ARN
  expectedOutputSchema?: string;
  requiresCarrierContext?: boolean;
  requiresCoverageTypeContext?: boolean;
  requiresWholesalerContext?: boolean;
  tags?: { [key: string]: string }; // Optional tags for the prompt
  defaultVariant?: PromptVariant; // The optional default variant configuration
}

// Designation is deliberately absent: it is immutable after creation. An
// omitted flag preserves the server's current value, so send all three
// explicitly when the user can have touched them.
export interface UpdatePromptRequest {
  name?: string;
  description?: string;
  systemPrompt?: string;
  userPrompt?: string;
  temperature?: number;
  maxTokens?: number;
  modelArn?: string;
  expectedOutputSchema?: string | null;
  requiresCarrierContext?: boolean;
  requiresCoverageTypeContext?: boolean;
  requiresWholesalerContext?: boolean;
}

export interface UpdatePromptResponse {
  id: string;
  name: string;
  version: string;
  updatedAt: string;
}

export interface DeletePromptResponse {
  id: string;
  version?: string;
  message: string;
}

export interface AIPromptResult {
  id: number;
  promptMessage: string;
  promptArn?: string;
  promptId?: number;
  promptName?: string;
  promptVersion?: string;
  // The prompt's internal/external classification. External results create a policy
  // through the binder flow rather than overriding an existing one.
  designation?: 'internal' | 'external';
  modelArn: string;
  documentName?: string;
  documentArn?: string;
  documentId?: number; // Integer ID from Document.ID
  documentAccountId?: number; // AccountID for platform document downloads
  documentAccountName?: string; // Account name resolved from document's AccountID
  documentUuid?: string; // UUID string from Document.DocumentID for download API
  userId: number;
  userEmail: string;
  rawOutput: string;
  isMalformed?: boolean;
  createdAt: string;
  queueStatus?: string;
}

export interface CreatePromptResultRequest {
  promptId: number;
  promptVersion: string;
  promptMessage: string;
  modelArn: string;
  documentName?: string;
  documentArn?: string; // For backward compatibility
  documentId?: string; // Platform document ID (UUID from Document.DocumentID)
  rawOutput: string;
}

export interface CreatePromptResultResponse {
  message: string;
}

export interface AIPromptMapping {
  id: number;
  promptId: number;
  promptVersion: string;
  coverageTypeId: number;
  documentTypeId: number;
}

export interface AIPromptMappingRequest {
  promptId: number;
  promptVersion: string;
  coverageTypeId: number;
  documentTypeId: number;
}

export interface AIPromptMappingUpdateRequest {
  promptId?: number;
  promptVersion?: string;
  coverageTypeId?: number;
  documentTypeId?: number;
}

// Sentinel value stored in promptVersion to mean "use the newest published version".
// The backend resolves this at extraction time. Keep in sync with entity.PromptVersionLatest in the API.
export const PROMPT_VERSION_LATEST = 'latest';

export interface BulkUpdatePromptMappingsRequest {
  ids: number[];
  promptId: number;
  promptVersion: string;
}

export interface BulkUpdatePromptMappingsResponse {
  updatedCount: number;
}

export interface PublishPromptVersionResponse {
  message: string;
  version: string;
}

export type ModelInvocationStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface ModelInvocation {
  invocationId: string;
  status: ModelInvocationStatus;
  modelArn: string;
  promptId?: number;
  promptVersion?: string;
  documentId?: number;
  documentName?: string;
  userId: number;
  userEmail: string;
  errorMessage?: string;
  promptResultId?: number;
  // From the prompt, not the invocation: an extraction that has not finished has no result
  // to read its designation off, and the list still has to mark the external ones.
  designation?: PromptDesignation;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface ModelInvocationStatusResponse {
  invocationId: string;
  status: ModelInvocationStatus;
  errorMessage?: string;
  completedAt?: string;
  promptResultId?: number;
}

export interface InvokeModelAsyncResponse {
  invocationId: string;
}

export type ExtractionFailureSource = 'MODEL_INVOCATION' | 'PROCESSING_QUEUE';
export type ExtractionFailureSourceTrigger = 'MANUAL' | 'BULK' | 'INTERNAL_UPLOAD' | 'EPIC_ETL';

export interface ExtractionFailure {
  id: number;
  source: ExtractionFailureSource;
  sourceTrigger: ExtractionFailureSourceTrigger;
  status: string;
  documentId?: number;
  documentName?: string;
  documentUuid?: string;
  userId: number;
  userEmail: string;
  errorMessage?: string;
  promptId?: number;
  promptVersion?: string;
  invocationId?: string;
  designation?: PromptDesignation;
  createdAt: string;
  completedAt?: string;
}

// Server-side filters for the /extraction-failures endpoint. accountSearch / promptName /
// userEmail mirror the same-named params on /prompt-results-with-reviews so the Extraction
// Runs page can share a single filter bar between the Completed and Failed tabs.
export interface ExtractionFailuresFilter {
  limit?: number;
  offset?: number;
  days?: number;
  accountSearch?: string;
  promptName?: string;
  userEmail?: string;
}

// Paginated envelope returned by /extraction-failures.
export interface ExtractionFailuresResponse {
  items: ExtractionFailure[];
  totalCount: number;
}

// Union type for ResultsPage display
export type ResultsPageItem = AIPromptResultWithReviews | ModelInvocation;
export type ExtendedResultsPageItem = ResultsPageItem | ExtractionFailure;

// =====================================================
// Field Override Types (CAC-5113: AI Field Override Review Mode)
// =====================================================

export type OverrideFieldType = 'string' | 'number' | 'boolean';

export interface FieldOverride {
  id: number;
  promptResultId: number;
  fieldPath: string;
  fieldType: OverrideFieldType;
  originalValue: string | number | boolean | null;
  overrideValue: string | number | boolean | null;
  isExcluded: boolean;
  userEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface FieldOverrideListResponse {
  overrides: FieldOverride[];
  totalCount: number;
}

// A single in-flight reviewer edit. One of value (a correction), isExcluded=true (drop the
// field from the override import), or isReverted=true (undo a prior review's override,
// returning the field to the LLM original) must be provided. Edits are submitted only when a
// review completes — there is no per-field persistence.
export interface ReviewEditInput {
  fieldPath: string;
  value?: string | number | boolean;
  isExcluded?: boolean;
  isReverted?: boolean;
}

// A structural coverage-line operation submitted with a review. Line keys are stable
// server-assigned identifiers stamped on the merged output: "raw-<N>" for lines the AI
// extracted, "add-<hex>" for reviewer-added lines. An add with no lineKey creates a new
// line; an add with an existing "add-" key supersedes that line's fields/type (how a later
// review edits an added line). delete/recategorize/restore require a lineKey.
export type LineOpType = 'add' | 'delete' | 'recategorize' | 'restore';

export interface LineFieldInput {
  name: string;
  value?: string | number | boolean;
}

export interface ReviewLineOpInput {
  operation: LineOpType;
  lineKey?: string;
  coverageTypeId?: number;
  fields?: LineFieldInput[];
}

// Request body for completing a review (POST .../review) and previewing its changes
// (POST .../review/preview).
export interface CreateReviewRequest {
  edits: ReviewEditInput[];
  lineOps?: ReviewLineOpInput[];
}

// restrictedColumns is keyed by override column name; restrictedFields by AI field name.
export interface PolicySourceRestriction {
  policySourceId: number;
  policySourceName: string;
  restrictedColumns: Record<string, string>;
  restrictedFields: Record<string, string>;
}

// One allowed target for coverage-line add/recategorize operations.
export interface LineCoverageTypeOption {
  id: number;
  name: string;
}

// Reference-data provenance for one extracted field, written by the agentic
// extraction engine (CAC-5634). Display-only.
export interface EntityMatch {
  fieldPath: string;
  entityType: 'carrier' | 'coverage_type' | 'wholesaler';
  matchedId?: string;
  matchedName?: string;
  sourceText?: string;
  status: 'matched' | 'llm_matched' | 'unmatched';
  source: string;
  // The group the matched entity rolls up to: the insurer group for a carrier, the
  // wholesaler group for a wholesaler. A policy line stores the child entity, so the group
  // is derived from the match and shown beside the correction rather than corrected itself.
  groupName?: string;
}

// What reviewing an extraction commits to. Derived server-side from the prompt's
// designation and the document's linkage, so a review cannot open in the wrong mode:
// the banner, the terminal action and the validation all key off `kind`.
export interface ExtractionTarget {
  kind: 'policy_override' | 'new_external_policy' | 'none';
  policyId?: string;
  policyNumber?: string;
  accountId?: number;
  accountName?: string;
  submissionId?: number;
  submissionStatus?: string;
  // What a confirmed submission produced, so a surface that cannot create a policy can
  // still say whether this extraction became one.
  createdPolicyId?: number;
  createdPolicyNumber?: string;
}

export interface MergedPromptResultResponse extends AIPromptResult {
  mergedOutput: string;
  overrideCount: number;
  overriddenFields: string[];
  excludedFields?: string[];
  entityMatches?: EntityMatch[];
  policySourceRestriction?: PolicySourceRestriction;
  // Allowed targets for coverage-line add/recategorize: the linked policy's uniquely-lined
  // coverage types ("policy" source), else the mapped prompt schema's templates ("schema"),
  // else empty ("none" — the structural line controls are unavailable).
  lineCoverageTypeOptions?: LineCoverageTypeOption[];
  lineCoverageTypeSource?: 'policy' | 'schema' | 'none';
  target?: ExtractionTarget;
}

// =====================================================
// Review Types (CAC-5113: Track who reviewed extraction results)
// =====================================================

// Review summary for list display
export interface ReviewSummary {
  userEmail: string;
  reviewedAt: string; // ISO 8601 datetime
}

// Full review record
export interface AIPromptResultReview {
  id: number;
  promptResultId: number;
  userId: number;
  userEmail: string;
  reviewedAt: string;
}

// Prompt result with review info (from /prompt-results-with-reviews)
export interface AIPromptResultWithReviews extends AIPromptResult {
  reviews: ReviewSummary[];
  reviewCount: number;
}

// Paginated envelope returned by /prompt-results-with-reviews
export interface PromptResultsWithReviewsResponse {
  results: AIPromptResultWithReviews[];
  totalCount: number;
}

// Server-side filters for the results-with-reviews endpoint
export interface PromptResultsFilter {
  pageSize?: number;
  pageOffset?: number;
  userHasServicingRole?: boolean;
  userFavorited?: boolean;
  accountSearch?: string;
  promptName?: string;
  userEmail?: string;
  showValidated?: boolean;
}

// Distinct filter dropdown options (from /prompt-results/filter-options)
export interface PromptResultFilterOptions {
  promptNames: string[];
  userEmails: string[];
}

// Response from marking as reviewed
export interface CreateReviewResponse {
  message: string;
  reviewedAt: string;
}

// =====================================================
// Monitoring Types (Binder Validation Status)
// =====================================================

export interface BinderValidationStatus {
  totalBinders: number;
  validatedCount: number;
  unvalidatedCount: number;
  exceptionCount: number;
  unprocessedCount: number;
}

export interface UnvalidatedBindersByCoverageType {
  coverageTypeId: number;
  coverageTypeName: string;
  count: number;
}

export interface ModelPerformance {
  modelName: string;
  modelArn: string;
  runs: number;
  reviewedRuns: number;
  totalReviews: number;
  avgReviewsPerRun: number;
  totalFieldsCaptured: number;
  avgFieldsPerRun: number;
  // timezone-less timestamp of the model's most recent run, e.g. "2026-06-02T06:00:00"
  lastRunAt: string;
}

export interface PromptAccuracy {
  promptId: number;
  promptName: string;
  promptVersion: string | null;
  reviewedResults: number;
  evaluatedFields: number;
  confirmedFields: number;
  editedFields: number;
  malformedResults: number;
  // null when no fields were evaluated in the window (no data, not 0%)
  accuracy: number | null;
  // timezone-less timestamp of the most recent counted review, e.g. "2026-06-02T06:00:00";
  // null for malformed-only groups
  lastReviewedAt: string | null;
}

export interface ReviewPreviewChange {
  field: string;
  currentValue: string | number | boolean | null;
  newValue: string | number | boolean | null;
  restricted?: boolean;
  restrictionReason?: string;
}

export interface LineReviewChange {
  coverageType: string;
  field: string;
  currentValue: string | number | boolean | null;
  newValue: string | number | boolean | null;
  restricted?: boolean;
  restrictionReason?: string;
}

// One structural coverage-line operation reflected in the preview. For add/recategorize,
// hasMatchingPolicyLine reports whether exactly one PolicyLine of the target coverage type
// exists on the policy — without one, the line is saved with the review but produces no
// PolicyLineOverride on import.
export interface LineStructureChange {
  operation: LineOpType;
  lineKey: string;
  coverageType: string;
  previousCoverageType?: string;
  hasMatchingPolicyLine: boolean;
}

// A sublimit or subretention the import would write to a coverage line. Import never removes
// sub-items, so there is no 'remove' operation; itemId and currentValue are set on updates only.
// coverageType matches LineReviewChange.coverageType, which is how it is grouped under its line.
export interface SubitemReviewChange {
  coverageType: string;
  kind: SubitemKind;
  typeId: number;
  typeName: string;
  operation: 'add' | 'update';
  itemId: number | null;
  currentValue: number | null;
  newValue: number;
}

export interface AccountReviewChange {
  field: string;
  currentValue: string | number | boolean | null;
  newValue: string | number | boolean | null;
}

export interface ReviewPreviewResponse {
  policyId: number | string;
  policyNumber: string;
  changes: ReviewPreviewChange[];
  accountChanges?: AccountReviewChange[];
  lineChanges?: LineReviewChange[];
  lineStructureChanges?: LineStructureChange[];
  subitemChanges?: SubitemReviewChange[];
  policySourceRestriction?: PolicySourceRestriction;
}

// =====================================================
// Bulk Invoke Types
// =====================================================

export interface BulkInvokeRequest {
  promptId: number;
  promptVersion: string;
  coverageTypeId: number;
  documentTypeId: number;
  includeUnprocessed: boolean;
  includeUnvalidated: boolean;
  includeExceptions: boolean;
  includeValidated: boolean;
  maxDocuments: number;
  effectiveDateFrom?: string;
  effectiveDateTo?: string;
}

export interface BulkInvokeResponse {
  queued: number;
  invocationIDs: string[];
}

export interface BulkInvokeCounts {
  unprocessed: number;
  unvalidated: number;
  exceptions: number;
  validated: number;
}
